import { useEffect, useState } from 'react'
import { Card, Chip, EmptyState, LoadingRows, relativeTime } from '../family-finance/shared'
import type { AuditRecord, Formula, FormulaCategory } from '../../types/fantasy-football'
import { formulasWithParams, type ParamMap } from '../../lib/fantasy-knowledge-base'
import { listAuditRecords, saveParams } from '../../lib/fantasy-api'
import type { FantasyState } from './useFantasy'

const CATEGORY_META: Record<FormulaCategory, { label: string; tone: 'blue' | 'green' | 'amber' | 'purple' | 'red' | 'slate' }> = {
  projection: { label: 'Projection', tone: 'blue' },
  risk: { label: 'Risk', tone: 'red' },
  schedule: { label: 'Schedule', tone: 'amber' },
  valuation: { label: 'Valuation', tone: 'green' },
  draft: { label: 'Draft', tone: 'purple' },
  acquisition: { label: 'Acquisition', tone: 'slate' },
}

export default function FormulasTab({ state }: { state: FantasyState }) {
  const { loading, params: savedParams, refresh } = state
  const [params, setParams] = useState<ParamMap>(savedParams)
  const [dirtyFormulas, setDirtyFormulas] = useState<Set<string>>(new Set())
  const [saving, setSaving] = useState(false)
  const [auditLog, setAuditLog] = useState<AuditRecord[]>([])
  const [expandedAudit, setExpandedAudit] = useState<string | null>(null)

  useEffect(() => setParams(savedParams), [savedParams])
  useEffect(() => {
    listAuditRecords().then(setAuditLog)
  }, [savedParams])

  if (loading) return <LoadingRows rows={8} />

  const formulas = formulasWithParams(params)

  function setParam(formulaId: string, key: string, value: number) {
    setParams((p) => ({ ...p, [key]: value }))
    setDirtyFormulas((s) => new Set(s).add(formulaId))
  }

  function resetFormula(formula: Formula) {
    setParams((p) => {
      const next = { ...p }
      for (const param of formula.parameters) next[param.key] = param.defaultValue
      return next
    })
    setDirtyFormulas((s) => new Set(s).add(formula.id))
  }

  async function handleSave() {
    setSaving(true)
    try {
      await saveParams(params, [...dirtyFormulas].sort())
      setDirtyFormulas(new Set())
      refresh()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-slate-500 dark:text-slate-400 text-sm">
          Every acquisition and draft recommendation traces to these formulas. Tune a parameter, save,
          and the whole app re-prices; each save is audit-logged with a full parameter snapshot.
        </p>
        <button
          onClick={handleSave}
          disabled={saving || dirtyFormulas.size === 0}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors shrink-0"
        >
          {saving ? 'Saving…' : dirtyFormulas.size > 0 ? `Save changes (${[...dirtyFormulas].join(', ')})` : 'No changes'}
        </button>
      </div>

      {formulas.map((formula) => (
        <Card
          key={formula.id}
          title={`${formula.id} — ${formula.name}`}
          action={<Chip tone={CATEGORY_META[formula.category].tone}>{CATEGORY_META[formula.category].label}</Chip>}
        >
          <div className="bg-slate-100 dark:bg-slate-900 rounded-lg px-4 py-3 font-mono text-sm text-emerald-700 dark:text-emerald-300 overflow-x-auto mb-3">
            {formula.formula}
          </div>
          <p className="text-slate-500 dark:text-slate-400 text-sm mb-3">{formula.rationale}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-1">
            <div>
              <p className="text-slate-500 text-xs uppercase font-medium mb-1">Inputs</p>
              <ul className="text-slate-500 dark:text-slate-400 text-xs space-y-1 list-disc list-inside">
                {formula.inputs.map((input, i) => (
                  <li key={i}>{input}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-slate-500 text-xs uppercase font-medium mb-1">Used in</p>
              <ul className="text-slate-500 dark:text-slate-400 text-xs space-y-1 list-disc list-inside">
                {formula.usedIn.map((u, i) => (
                  <li key={i}>{u}</li>
                ))}
              </ul>
            </div>
          </div>
          {formula.parameters.length > 0 && (
            <div className="mt-4 border-t border-slate-200/60 dark:border-slate-700/60 pt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-slate-500 text-xs uppercase font-medium">Tunable parameters</p>
                <button
                  onClick={() => resetFormula(formula)}
                  className="text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 text-xs"
                >
                  Reset defaults
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                {formula.parameters.map((param) => (
                  <div key={param.key}>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-slate-600 dark:text-slate-300 text-sm">{param.label}</label>
                      <span className={`text-sm font-medium tabular-nums ${param.value !== param.defaultValue ? 'text-amber-600 dark:text-amber-400' : 'text-white'}`}>
                        {param.value}
                        {param.value !== param.defaultValue && (
                          <span className="text-slate-500 text-xs ml-1">(default {param.defaultValue})</span>
                        )}
                      </span>
                    </div>
                    <input
                      type="range"
                      min={param.min}
                      max={param.max}
                      step={param.step}
                      value={param.value}
                      onChange={(e) => setParam(formula.id, param.key, Number(e.target.value))}
                      className="w-full accent-blue-500"
                    />
                    <p className="text-slate-500 text-xs mt-0.5">{param.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      ))}

      <Card
        title="Decision audit log"
        subtitle="Every generated order, approval, acquisition, and parameter change — with the exact parameter snapshot used"
      >
        {auditLog.length === 0 ? (
          <EmptyState icon="🗂️" message="No decisions recorded yet. Generate a draft order or tune a formula." />
        ) : (
          <div className="space-y-2">
            {auditLog.map((record) => (
              <div key={record.id} className="bg-slate-100/60 dark:bg-slate-900/60 rounded-lg px-4 py-3">
                <button
                  onClick={() => setExpandedAudit(expandedAudit === record.id ? null : record.id)}
                  className="w-full flex items-center justify-between gap-3 text-left"
                >
                  <div className="min-w-0">
                    <p className="text-slate-900 dark:text-white text-sm">{record.detail}</p>
                    <p className="text-slate-500 text-xs mt-0.5">
                      {record.action} · {relativeTime(record.timestamp)}
                      {record.formulaIds.length > 0 && ` · formulas: ${record.formulaIds.join(', ')}`}
                    </p>
                  </div>
                  <span className="text-slate-500 text-xs shrink-0">
                    {expandedAudit === record.id ? '▲ hide snapshot' : '▼ snapshot'}
                  </span>
                </button>
                {expandedAudit === record.id && (
                  <pre className="mt-3 bg-slate-200 dark:bg-slate-950 rounded-lg p-3 text-xs text-slate-500 dark:text-slate-400 overflow-x-auto">
                    {Object.keys(record.parameterSnapshot).length === 0
                      ? 'No parameters involved in this action.'
                      : Object.entries(record.parameterSnapshot)
                          .map(([k, v]) => `${k} = ${v}`)
                          .join('\n')}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}
